/* eslint-disable react/jsx-key */
import React, { useEffect, useState, useCallback } from 'react';
import { connect } from 'react-redux';
import { useQuery } from 'react-query';
import dayjs from 'dayjs';
import { Button, Divider, Form, message, Skeleton } from 'antd-v5';
import usePlanAndLDFlag from '../../../../../hooks/usePlanAndLDFlag';
import { useHistory } from 'react-router';
import StandardLayoutV2 from '../../../../Layout/StandardLayoutV2';
import { getRequest, saveListItem } from '../../../../../core/apiRequests';
import { StoreHours } from './components/store-hours';
import { StoreDetails } from './components/store-details';
import { StoreNotifications } from './components/notifications';
import { StoreMessaging } from './components/store-messaging';
import { StoreAppointments } from './components/store-appointments';
import { getStoreHours } from '../../../../../services/stores';

const daysOfWeek = [
  { label: 'Sunday', day: 'sun' },
  { label: 'Monday', day: 'mon' },
  { label: 'Tuesday', day: 'tues' },
  { label: 'Wednesday', day: 'wed' },
  { label: 'Thursday', day: 'thur' },
  { label: 'Friday', day: 'fri' },
  { label: 'Saturday', day: 'sat' },
];

const formatTime = time => {
  if (`${time}`.length > 3) {
    const first = String(time).slice(0, 2);
    const second = String(time).slice(2, 4);
    return `${first}:${second}`;
  }
  if (`${time}`.length === 2) {
    return `00:${time}`;
  }
  if (`${time}`.length === 1) {
    return `00:0${time}`;
  }
  const first = String(time).slice(0, 1);
  const second = String(time).slice(1, 3);
  return `0${first}:${second}`;
};

const EditStore = ({ auth, match }) => {
  const history = useHistory();
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const appointments = usePlanAndLDFlag('appointments');

  const { data: managers } = useQuery(
    ['storeManagers', auth.merchantId, match.params.storeId],
    async () => {
      const { merchantId } = auth;
      const { storeId } = match.params;
      const reqObj = {
        params: ['stores', 'managers', merchantId, storeId],
        query: {},
        loadingStatusOff: true,
      };
      const data = await getRequest(reqObj);
      const formattedManagerList = data.map(manager => ({
        label: `${manager?.first_name || ''} ${manager?.last_name || ''}`,
        value: manager.id,
      }));
      return formattedManagerList;
    },
    {
      enabled: !!auth.merchantId && !!match.params.storeId,
      refetchOnWindowFocus: false,
      initialData: [],
    },
  );

  const { data: associatesList } = useQuery(
    'associates',
    async () => {
      const reqObj = {
        params: ['chats', 'getAsctList'],
        loadingStatusOff: true,
      };
      const data = await getRequest(reqObj);
      const sortedAscts = data
        ?.sort((a, b) => {
          if (`${a.first_name || ''}${a.last_name || ''}` > `${b.first_name || ''}${b.last_name}`) {
            return 1;
          }
          if (`${a.first_name || ''}${a.last_name || ''}` < `${b.first_name || ''}${b.last_name}`) {
            return -1;
          }
          return 0;
        })
        .map(asct => ({
          value: asct?.id,
          label: `${asct?.first_name || ''} ${asct?.last_name || ''}`,
        }));
      return sortedAscts;
    },
    {
      enabled: !!auth.merchantId,
      refetchOnWindowFocus: false,
      initialData: [],
    },
  );

  useEffect(() => {
    if (auth.merchantId && auth.stores.length > 0) {
      const storeIds = auth.stores.map(store => store.id);

      if (
        !(
          Number(match.params.storeId) &&
          storeIds.includes(Number(match.params.storeId)) &&
          window.location.href.includes('edit-store')
        )
      ) {
        history.push('/Stores');
      }
    }
  }, [auth, match, history]);

  const {
    isFetching,
    data: storeDetails,
    refetch,
  } = useQuery(
    ['stores', 'details', auth.merchantId, match.params.storeId],
    async () => {
      const reqObj = {
        params: ['stores', 'details', auth.merchantId, match.params.storeId],
        loadingStatusOff: true,
      };
      const data = await getRequest(reqObj);
      if (data && data[0].id) {
        let timesObj = {};
        const times = data[0].times.reduce((acc, time) => ({ ...acc, ...time }), {});
        daysOfWeek.forEach(day => {
          const open = times[`${day.day}_open`];
          const close = times[`${day.day}_close`];
          timesObj = {
            ...timesObj,
            [day.day]: !!open && !!close,
          };
          if (!!open && !!close) {
            const year = new Date().getFullYear();
            const month = new Date().getMonth();
            const dayDate = String(new Date().getDate()).padStart(2, '0');
            const [openHour, openMinute] = formatTime(open).split(':');
            const [closeHour, closeMinute] = formatTime(close).split(':');
            const openDate = new Date(year, month, dayDate, openHour, openMinute, 0);
            const closeDate = new Date(year, month, dayDate, closeHour, closeMinute, 0);
            timesObj[`${day.day}_hours`] = [dayjs(openDate), dayjs(closeDate)];
          } else {
            timesObj[`${day.day}_hours`] = [];
          }
        });
        form.setFieldsValue({
          ...data[0],
          open_hours: { ...timesObj },
          appointments_min_hours_ahead: data[0]?.appointments_min_hours_ahead ?? 24,
        });
        return {
          ...data[0],
          open_hours: { ...timesObj },
        };
      }
      history.push('/Stores');
    },
    {
      enabled: !!auth.merchantId && !!match.params.storeId,
      refetchOnWindowFocus: false,
    },
  );

  const {
    data: storeHours,
    isFetching: isFetchingStoreHours,
    refetch: refetchStoreHours,
  } = useQuery(
    ['store', 'hours', match.params.storeId],
    async () => {
      try {
        const hours = await getStoreHours({ storeId: match.params.storeId });
        let timesObj = {};
        daysOfWeek.forEach(day => {
          const open = hours[`${day.label.toLocaleLowerCase()}Open`];
          const close = hours[`${day.label.toLocaleLowerCase()}Close`];
          timesObj = {
            ...timesObj,
            [day.day]: open != null && close != null,
          };
          if (open != null && close != null) {
            const year = new Date().getFullYear();
            const month = new Date().getMonth();
            const dayDate = String(new Date().getDate()).padStart(2, '0');
            const [openHour, openMinute] = formatTime(open).split(':');
            const [closeHour, closeMinute] = formatTime(close).split(':');
            const openDate = new Date(year, month, dayDate, openHour, openMinute, 0);
            const closeDate = new Date(year, month, dayDate, closeHour, closeMinute, 0);
            timesObj[`${day.day}_hours`] = [dayjs(openDate), dayjs(closeDate)];
          } else {
            timesObj[`${day.day}_hours`] = [];
          }
        });

        form.setFieldsValue({
          open_hours: { ...timesObj },
        });
        return {
          open_hours: { ...timesObj },
        };
      } catch (error) {
        console.error('Error fetching store hours:', error);
        return {
          fri: false,
          mon: false,
          sat: false,
          sun: false,
          thur: false,
          tues: false,
          wed: false,
          fri_hours: [],
          mon_hours: [],
          sat_hours: [],
          sun_hours: [],
          thur_hours: [],
          tues_hours: [],
          wed_hours: [],
        };
      }
    },
    {
      enabled: !!match.params.storeId,
      refetchOnWindowFocus: false,
      initialData: {
        fri: false,
        mon: false,
        sat: false,
        sun: false,
        thur: false,
        tues: false,
        wed: false,
        fri_hours: [],
        mon_hours: [],
        sat_hours: [],
        sun_hours: [],
        thur_hours: [],
        tues_hours: [],
        wed_hours: [],
      },
    },
  );

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      form.validateFields().then(async values => {
        const { firstName, lastName, merchantId } = auth;
        const fn = firstName || '';
        const ln = lastName || '';
        const times = {};
        daysOfWeek.forEach(day => {
          if (values.open_hours[day.day]) {
            const hours = values.open_hours[`${day.day}_hours`];
            if (hours && hours.length === 2) {
              times[`${day?.day?.slice(0, 3)}_startTime`] = hours[0]?.format('HHmm');
              times[`${day?.day?.slice(0, 3)}_endTime`] = hours[1]?.format('HHmm');
            } else {
              times[`${day?.day?.slice(0, 3)}_startTime`] = null;
              times[`${day?.day?.slice(0, 3)}_endTime`] = null;
            }
          } else {
            times[`${day?.day?.slice(0, 3)}_startTime`] = null;
            times[`${day?.day?.slice(0, 3)}_endTime`] = null;
          }
        });
        // Normalize phone_number to string format: +countryCodeareacodenumber
        let normalizedPhoneNumber = values.phone_number;
        if (values.phone_number && typeof values.phone_number === 'object') {
          const { countryCode, areaCode, phoneNumber } = values.phone_number;
          normalizedPhoneNumber = `+${countryCode}${areaCode}${phoneNumber}`;
        }

        const configObj = {
          id: match.params.storeId,
          ...values,
          phone_number: normalizedPhoneNumber,
          times,
          default_manager: values.manager_id || null,
          appointments_enabled: values?.appointments_enabled || false,
          appointments_virtual_enabled: values?.appointments_virtual_enabled || false,
          appointments_select_associate: values?.appointments_select_associate || false,
          appointments_min_hours_ahead: values?.appointments_min_hours_ahead ?? 24,
          use_assigned_associate: values?.use_assigned_associate || false,
        };

        const data = await saveListItem('stores', merchantId, configObj, `${fn} ${ln}`);
        refetch();
        refetchStoreHours();
        message.success('Store settings saved');
        history.push('/Stores');
      });
    } catch (e) {
      console.error(e);
      message.error('There was an error saving your store. Please try again.');
    } finally {
      setSaving(false);
    }
  }, []);

  return (
    <StandardLayoutV2
      title="Stores"
      breadcrumb={[{ title: 'Stores', path: '/Stores' }, { title: 'Edit Store' }]}
      classProps="flex-col-left"
      maxWidth="920px"
      styleProps={{ justifyContent: 'flex-start' }}
      containerStyles={{ backgroundColor: 'transparent', paddingRight: '32px', width: '100%' }}
      actions={
        <Button type="primary" onClick={handleSave} loading={saving} disabled={isFetching}>
          Save
        </Button>
      }
    >
      {isFetching || isFetchingStoreHours ? (
        <Skeleton active />
      ) : (
        <Form
          layout="vertical"
          style={{ width: '100%' }}
          form={form}
          initialValues={{ ...storeDetails, ...storeHours }}
        >
          <StoreDetails />
          <Divider style={{ margin: '40px 0' }} />
          <StoreHours daysOfWeek={daysOfWeek} form={form} storeId={match.params.storeId} />
          <Divider style={{ margin: '40px 0' }} />
          <StoreNotifications form={form} managers={managers} associatesList={associatesList} />
          <Divider style={{ margin: '40px 0' }} />
          <StoreMessaging merchantId={auth.merchantId} storeId={match.params.storeId} managers={managers} form={form} />
          {appointments && (
            <>
              <Divider style={{ margin: '40px 0' }} />
              <StoreAppointments form={form} />
            </>
          )}
        </Form>
      )}
    </StandardLayoutV2>
  );
};

const mapStateToProps = state => ({
  auth: state.auth,
  pagination: state.pagination,
});
export default connect(mapStateToProps, {})(EditStore);
